import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Type } from '@angular/core';
import { BasicWidgetInfoDevMode, PlThemeColors } from '@gspwidget/util';
import { WidgetComponent } from '@gspwidget/widget-core';
import { PropertyOption } from '@gspwidget/widget-devkit';
import { paramCase } from 'change-case';
import { AutocompleteDataSource } from 'ng-zorro-antd/auto-complete';
import { Observable, of } from 'rxjs';
import { debounceTime, delay, tap } from 'rxjs/operators';

if (!window['DashboardApi']) window['DashboardApi'] = {}

const PROP_VALUES_STORAGE_KEY = 'PROP_VALUES'
const CHECKED_WIDGETS_STORAGE_KEY = 'CHECKED_WIDGETS'
const MOCK_DATA = [
  { month: '1æ', totalSale: 100, lastYear: 120, area1: 450, area2: 90, area3: 60 },
  { month: '2æ', totalSale: 300, lastYear: 220, area1: 280, area2: 70, area3: 70 },
  { month: '3æ', totalSale: 400, lastYear: 380, area1: 130, area2: 90, area3: 80 },
  { month: '4æ', totalSale: 300, lastYear: 420, area1: 180, area2: 75, area3: 75 },
  { month: '5æ', totalSale: 500, lastYear: 290, area1: 300, area2: 100, area3: 60 },
  { month: '6æ', totalSale: 200, lastYear: 350, area1: 80, area2: 60, area3: 64 },
  { month: '7æ', totalSale: 400, lastYear: 220, area1: 280, area2: 60, area3: 72 }
]
const pureColors = [
  '#F5B805', '#FF9300', '#EC7658', '#897AFB', '#5E6EEB',
  '#4D97FF', '#01BBC7', '#3DDB88', '#A4EA4D', '#93A3D3'
]
const gradientColors = [
  '#FBDD0C,#F5B805',
  '#FFB046,#FF9300',
  '#FF9579,#EC7658',
  '#8B99FF,#897AFB',
  '#7F8DFF,#5E6EEB',
  '#79B1FF,#4D97FF',
  '#00DBE9,#01BBC7',
  '#46FA9B,#3DDB88',
  '#C1FF73,#A4EA4D',
  '#B5C7FD,#93A3D3',
]

interface WidgetInfoAll extends BasicWidgetInfoDevMode {
  allPropValues?: object,
  properties?: PropertyOption[],
}
interface WidgetInfoDebug extends WidgetInfoAll {
  checked?: boolean
}

@Component({
  selector: 'widget-project-preview-page',
  templateUrl: './widget-project-preview-page.component.html',
  styleUrls: ['./widget-project-preview-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WidgetProjectPreviewPageComponent implements OnInit {
  @Input() widgetComponents: Type<any>[] = []
  @Input() getData: () => Observable<any> = null
  // @Input() title = 'widget-examples'
  widgetInfosAll: WidgetInfoDebug[] = null
  widgetPropValuesMap: { [widgetName: string]: object } = null
  curEditingWidgetInfoAll: WidgetInfoAll = null
  curEditingWC: WidgetComponent = null
  checkedWidgets = []
  copySuccessStatus = false

  innerGetData = () => (this.getData ? this.getData() : of(MOCK_DATA))
    .pipe(tap(data => {
      this.dataFields = Object.keys(data[0])
    }), delay(1))
  dataFields: AutocompleteDataSource = []

  private savePropValuesDebounce = new EventEmitter()

  constructor (
    private cd: ChangeDetectorRef,
  ) {
    this.savePropValuesDebounce.pipe(debounceTime(5000)).subscribe(() => {
      localStorage.setItem(PROP_VALUES_STORAGE_KEY, JSON.stringify(this.widgetPropValuesMap))
    })
    PlThemeColors.SetColors(pureColors, gradientColors)
  }

  ngOnInit () {
    this.widgetInfosAll = this.widgetComponents.map(widgetComponent => {
      return {
        widgetComponent,
        name: paramCase(widgetComponent.name).replace('-component', ''), // è¿éå¯è½åçç®å½åæ¨æµéè¯¯é®é¢ï¼å¨ææ¡£ä¸­æ³¨æå½åè§è
        version: 'DEV_PREVIEW'
      }
    })
    // prop values
    const propValuesInStorage = JSON.parse(localStorage.getItem(PROP_VALUES_STORAGE_KEY))
    if (!propValuesInStorage) {
      this.widgetPropValuesMap = {}
      this.widgetInfosAll.forEach(widgetInfoAll => {
        this.widgetPropValuesMap[widgetInfoAll.name] = {}
      })
    } else {
      this.widgetPropValuesMap = propValuesInStorage
    }
    // check
    const checkedWidgetNames = JSON.parse(localStorage.getItem(CHECKED_WIDGETS_STORAGE_KEY)) || []
    checkedWidgetNames.forEach(wName => {
      const checkW = this.widgetInfosAll.find(w => w.name === wName)
      if (checkW) checkW.checked = true
    })
    this._refreshCheckStatus()
  }

  handleClickWidget (widgetInfoAll: WidgetInfoAll, wc: WidgetComponent) {
    this.curEditingWidgetInfoAll = widgetInfoAll
    this.curEditingWC = wc
  }

  handleChangePropertyValue (e) {
    const { propName, value } = e
    const { name } = this.curEditingWidgetInfoAll
    this.curEditingWC.setPropertyValue(propName, value)
    if (!this.widgetPropValuesMap[name]) this.widgetPropValuesMap[name] = {}
    this.widgetPropValuesMap[name][propName] = value
    this.savePropValuesDebounce.emit()
  }

  handleWidgetLoaded (widgetInfo: WidgetInfoDebug, widgetData) {
    widgetInfo.properties = widgetData.properties
    widgetInfo.allPropValues = widgetData.propValues
  }
  handleWidgetCheckChange (widgetInfo: WidgetInfoDebug, checked: boolean) {
    widgetInfo.checked = checked
    this._refreshCheckStatus()
  }
  handleCheckAllChange (checked: boolean) {
    this.widgetInfosAll.forEach(w => w.checked = checked)
    this._refreshCheckStatus()
  }
  copyBuildCmd () {
    navigator['clipboard'].writeText(this.getBuildCommand()).then(() => {
      this.copySuccessStatus = true
      this.cd.markForCheck()
      setTimeout(() => {
        this.copySuccessStatus = false
        this.cd.markForCheck()
      }, 800)
    })
  }

  private _refreshCheckStatus () {
    this.checkedWidgets = this.widgetInfosAll.filter(w => w.checked)
    const checkedWidgetNames = this.checkedWidgets.map(w => w.name)
    localStorage.setItem(CHECKED_WIDGETS_STORAGE_KEY, JSON.stringify(checkedWidgetNames))
  }

  /** çæ build å½ä»¤ */
  getBuildCommand () {
    return this.checkedWidgets
      .map(w => `widget build ${w.name}`)
      .join('; ')
  }
}
